import { z } from 'zod';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminFacilityProposal, FacilityProposalStatus } from '@mds/shared/types/proposal';

import type { PaymentChoice } from '@/domain/bookings';
import { REQUIRED, picked, requiredText } from '@/lib/validation';

/** How a proposal's status reads, and the classes to draw it in. */

const LABELS: Record<FacilityProposalStatus, string> = {
  submitted: 'New',
  under_review: 'In review',
  awaiting_payment: 'Awaiting payment',
  converted_to_booking: 'Booked',
  rejected: 'Rejected',
};

const TONES: Record<FacilityProposalStatus, string> = {
  submitted: 'border-warn-edge bg-warn-wash text-warn-text',
  under_review: 'border-hairline bg-surface text-ink-secondary',
  awaiting_payment: 'border-mint/35 bg-mint-wash text-mint-deep',
  converted_to_booking: 'border-brand-edge/35 bg-brand-wash text-brand-strong',
  rejected: 'border-hairline bg-raised text-ink-muted',
};

export const proposalStatusLabel = (status: FacilityProposalStatus) => LABELS[status];
export const proposalStatusTone = (status: FacilityProposalStatus) => TONES[status];

/** Rejection is open from every status before a booking exists. */
export const isOpen = (status: FacilityProposalStatus) =>
  status !== 'converted_to_booking' && status !== 'rejected';

const errorCode = (error: unknown): string | null =>
  error instanceof ApiError ? (error.code ?? null) : null;

/**
 * The failures that mean "what you are looking at is out of date". The only
 * sensible response to each is to reload the proposal and decide again.
 */
export const isStale = (error: unknown) => {
  const code = errorCode(error);
  return (
    code === 'PROPOSAL_STATE_CHANGED' ||
    code === 'PROPOSAL_QUOTE_CHANGED' ||
    code === 'BOOKING_CONFLICT'
  );
};

/** A failure, phrased for the administrator. */
export function proposalProblem(error: unknown, fallback: string): string {
  switch (errorCode(error)) {
    case 'PROPOSAL_STATE_CHANGED':
      return 'Someone else changed this proposal while you had it open. It has been reloaded. Check it and try again.';
    case 'PROPOSAL_QUOTE_CHANGED':
      return 'The price changed since the quote was taken. The proposal has been reloaded; refresh the quote before taking payment.';
    case 'BOOKING_CONFLICT':
      return 'That time is no longer free, because something else was booked into it. The proposal has been reloaded.';
    case 'PROPOSAL_CAPACITY_EXCEEDED':
      return 'More people are coming than the room holds.';
    case 'BOOKING_TIME_PAST':
      return 'The requested time has already passed.';
    case 'PROPOSAL_PRICE_UNAVAILABLE':
      return 'The rate card has no price for this room right now. Fix it on Packages & pricing first.';
    default:
      return error instanceof ApiError ? error.message : fallback;
  }
}

/** A rejection is emailed to the guest, so it has to say why. */
export const rejectionSchema = z.object({ reason: requiredText(1_000) });

type Quote = NonNullable<AdminFacilityProposal['quote']>;

/** The payment the chosen option takes, in cents. */
export const quotedAmount = (quote: Quote, choice: PaymentChoice) =>
  choice === 'deposit' ? quote.requiredDepositAmountMinor : quote.totalAmountMinor;

/**
 * The payment that turns a proposal into a booking. The amount is never typed —
 * it is the quote's deposit or its total — but the API still refuses a payment
 * of nothing, and a method of "other" has to say what it was.
 */
export const proposalPaymentSchema = (quote: Quote | null) =>
  z
    .object({
      choice: z.enum(['deposit', 'full']),
      method: picked('Choose how it was paid.'),
      methodDetail: z.string(),
    })
    .superRefine((draft, context) => {
      if (quote && quotedAmount(quote, draft.choice) <= 0) {
        context.addIssue({
          code: 'custom',
          path: ['choice'],
          message:
            'There is nothing to take on this option. Refresh the quote, or take the full amount.',
        });
      }
      if (draft.method !== 'other') return;
      const detail = draft.methodDetail.trim();
      const message =
        detail === '' ? REQUIRED : detail.length > 80 ? 'Keep it to 80 characters or fewer.' : null;
      if (message) context.addIssue({ code: 'custom', path: ['methodDetail'], message });
    });
